import moment from "moment";
import { formatAddress, formatClientAddress } from "../Clients/clients";
import * as actions from "./actions";

const initialState = {};

/**
 * Back-fills the `codcli` field on carts that were stored before this field
 * was introduced (e.g. carts migrated from old localStorage snapshots).
 * The Redux state is keyed by creator (= CODCLI), so we can always recover it.
 */
function backfillCodecli(state) {
  let changed = false;
  const next = { ...state };
  Object.keys(next).forEach((creator) => {
    const entry = next[creator];
    if (!entry) return;
    const fixedCarts = (entry.carts || []).map((cart) => {
      if (cart.codcli) return cart;
      changed = true;
      return {
        ...cart,
        codcli: cart.client?.CODCLI ?? creator,
      };
    });
    const fixedCurrent =
      entry.current_cart && !entry.current_cart.codcli
        ? {
            ...entry.current_cart,
            codcli: entry.current_cart.client?.CODCLI ?? creator,
          }
        : entry.current_cart;
    if (changed) {
      next[creator] = {
        ...entry,
        carts: fixedCarts,
        current_cart: fixedCurrent,
      };
    }
  });
  return changed ? next : state;
}

export default function (state = initialState, action) {
  switch (action.type) {
    case actions.CREATE_CART: {
      const newCart = {
        _id: action.payload.id,
        created_at: action.payload.created_at || moment(),
        client: action.payload.client,
        products: action.payload.products || {},
        packs: action.payload.packs || [],
        codcli: action.payload.codcli || action.payload.creator,
      };

      const creator = action.payload.creator;

      const existingCarts = (state[creator] && state[creator].carts) || [];

      const filteredCarts = existingCarts.filter(
        (cart) => cart._id !== newCart._id,
      );

      return {
        ...state,
        [creator]: {
          carts: [...filteredCarts, newCart],
          current_cart: newCart,
        },
      };
    }

    case actions.REPLACE_CART_ID: {
      const { creator, oldId, newId } = action.payload;
      const creatorState = state[creator];
      if (!creatorState) {
        console.log(
          "Nothing to replace if there's no such creator",
          action.payload,
        );
        return state;
      }

      const { carts: oldCarts = [], current_cart } = creatorState;

      const newCarts = oldCarts.map((cart) => {
        if (cart._id === oldId) {
          return {
            ...cart,
            _id: newId,
          };
        }
        return cart;
      });

      let newCurrentCart = current_cart;
      if (current_cart && current_cart._id === oldId) {
        newCurrentCart = {
          ...current_cart,
          _id: newId,
        };
      }
      return {
        ...state,
        [creator]: {
          ...creatorState,
          carts: newCarts,
          current_cart: newCurrentCart,
        },
      };
    }

    case actions.CUSTOM_REMISE: {
      const { creator, libgen, codart, remise } = action.payload;
      const cartObj = state[creator]?.current_cart;
      if (!cartObj) return state;

      const arr = cartObj.products[libgen];
      if (!Array.isArray(arr)) return state;

      const indexArt = arr.findIndex((art) => art.article.CODART === codart);
      if (indexArt >= 0) {
        // Make immutable update
        const updatedArt = {
          ...arr[indexArt],
          custom_remise: remise,
        };
        const newArr = [...arr];
        newArr[indexArt] = updatedArt;

        const newProducts = {
          ...cartObj.products,
          [libgen]: newArr,
        };

        const newCurrent = {
          ...cartObj,
          products: newProducts,
        };

        const newCarts = (state[creator].carts || []).map((cart) =>
          cart._id === newCurrent._id ? newCurrent : cart,
        );

        return {
          ...state,
          [creator]: {
            ...state[creator],
            carts: newCarts,
            current_cart: newCurrent,
          },
        };
      }
      return state;
    }

    case actions.ADD_IN_CART: {
      const { creator, product } = action.payload;
      const cartObj = state[creator]?.current_cart;
      if (!cartObj) return state;

      const newCurrentCart = {
        ...cartObj,
        products: {
          ...cartObj.products,
          ...product,
        },
      };

      const newCarts = (state[creator].carts || [])
        .filter((cart) => cart._id !== newCurrentCart._id)
        .concat(newCurrentCart);

      return {
        ...state,
        [creator]: {
          ...state[creator],
          carts: newCarts,
          current_cart: newCurrentCart,
        },
      };
    }

    case actions.SELECT_CART: {
      const { creator, _id } = action.payload;
      const cartList = state[creator]?.carts || [];
      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: cartList.find((cart) => cart._id === _id) || null,
        },
      };
    }

    case actions.REMOVE_CART: {
      const { creator, _id } = action.payload;
      const filtered = (state[creator]?.carts || []).filter(
        (cart) => cart._id !== _id,
      );
      return {
        ...state,
        [creator]: {
          carts: filtered,
          current_cart: null,
        },
      };
    }

    case actions.REMOVE_ALL_CART: {
      return {
        ...state,
        ...Object.keys(state).reduce((acc, el) => {
          acc[el] = {
            carts: [],
            current_cart: null,
          };
          return acc;
        }, {}),
      };
    }

    case actions.REMOVE_CART_FROM_CART_LIST: {
      const { creator, _id } = action.payload;
      const filtered = (state[creator]?.carts || []).filter(
        (cart) => cart._id !== _id,
      );
      return {
        ...state,
        [creator]: {
          carts: filtered,
          current_cart: null,
        },
      };
    }

    case actions.CREATE_HEADER: {
      const { client, creator, remise, frais_port } = action.payload;
      const oldStateForCreator = state[creator] || {};
      const oldCurrent = oldStateForCreator.current_cart || {};

      // Build addresses and header logic
      let defaultAddrLiv;
      let defaultAddrFac;
      const addressesLivraison = [];
      const addressFacturation = [];

      const clientAddress = formatClientAddress(client);
      if (client.ADRFAC === "O") {
        addressFacturation.push({ key: client.CODCLI, value: clientAddress });
      }
      if (client.ADRLIV === "O") {
        addressesLivraison.push({ key: client.CODCLI, value: clientAddress });
      }
      if (client.ADRFAC_DFT === "O") {
        defaultAddrFac = client.CODCLI;
      }
      if (client.ADRLIV_DFT === "O") {
        defaultAddrLiv = client.CODCLI;
      }

      client.adresses
        .filter((el) => el.ACTIF !== "N")
        .forEach((element) => {
          const addr = formatAddress(element);
          if (element.ADRFAC === "O") {
            addressFacturation.push({ key: element.CODADR, value: addr });
          }
          if (element.ADRLIV === "O") {
            addressesLivraison.push({ key: element.CODADR, value: addr });
          }
          if (element.ADRFAC_DFT === "O") {
            defaultAddrFac = element.CODADR;
          }
          if (element.ADRLIV_DFT === "O") {
            defaultAddrLiv = element.CODADR;
          }
        });

      defaultAddrFac = defaultAddrFac;
      defaultAddrLiv = defaultAddrLiv;

      const newHeader = {
        clientName: client.NOMCLI,
        PROSPECT: client.PROSPECT,
        CODCLI: client.CODCLI,
        CODADR_LIV: defaultAddrLiv,
        CODADR_FAC: defaultAddrFac,
        addresses: [
          { key: client.CODCLI, value: clientAddress },
          ...client.adresses.map((v) => ({
            key: v.CODADR,
            value: formatAddress(v),
          })),
        ],
        addressesLivraison,
        addressFacturation,
        MT_REMISE: remise,
        MT_PORT: frais_port,
        COMMENTAIRE_LIBRE: "",
        DATE_LIV: "",
      };

      const newCurrent = {
        ...oldCurrent,
        header: newHeader,
      };

      return {
        ...state,
        [creator]: {
          ...oldStateForCreator,
          current_cart: newCurrent,
        },
      };
    }

    case actions.CHANGE_FEET_REMISE: {
      const { creator, remise } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            header: {
              ...curr.header,
              MT_REMISE: parseFloat(remise || 0),
            },
          },
        },
      };
    }

    case actions.REMOVE_ORDER_HEADER: {
      const { creator } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            header: null,
          },
        },
      };
    }

    case actions.ADD_PACK_IN_CART: {
      const { creator, packs } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            packs,
          },
        },
      };
    }

    case actions.REMOVE_PACK_FROM_CART: {
      const { creator, packName } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            packs: curr.packs.filter((pack) => pack.name !== packName),
            products: {
              ...curr.products,
              [packName]: [],
            },
          },
        },
      };
    }

    case actions.GENERATE_ORDER_SUCCESS: {
      const { creator, generated_at, email } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            email_receiver: email,
            generated: true,
            generated_at,
          },
        },
      };
    }

    case actions.CHANGE_CART_HEADER: {
      const { creator, key, value } = action.payload;
      const curr = state[creator]?.current_cart;
      if (!curr) return state;

      return {
        ...state,
        [creator]: {
          ...state[creator],
          current_cart: {
            ...curr,
            header: {
              ...curr.header,
              [key]: value,
            },
          },
        },
      };
    }
    case actions.UPDATE_CART_AS_LANG: {
      const { creator, carts, current_cart } = action.payload;
      state = {
        ...state,
        [creator]: {
          ...state[creator],
          carts: carts,
          current_cart: current_cart,
        },
      };
      return state;
    }
    case actions.UPDATE_CART_LOADING_STATUS: {
      const { isLoad } = action.payload;
      return {
        ...state,
        isCartsLoaded: isLoad,
      };
    }

    default:
      // On @@INIT (state hydration from storage), heal any carts that are
      // missing the `codcli` field (migrated from old localStorage format).
      return backfillCodecli(state);
  }
}
